import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { ReactFlow, addEdge, useNodesState, useEdgesState, Background, Controls, MiniMap, Handle, Position, MarkerType } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Play, MessageCircle, Image, Video, Mic, MousePointerClick, Clock, GitBranch,
  Sparkles, Square, Plus, Save, Trash2, Copy, Download, Upload, ChevronLeft,
  Send, X, RotateCcw, Settings, FileText, List, Zap, Bot, Check, Users, Pencil, Briefcase
} from "lucide-react";

// ===== CUSTOM NODES =====
const nodeColors = {
  startNode: { bg: "#16a34a", border: "#15803d", text: "white" },
  messageNode: { bg: "#2563eb", border: "#1d4ed8", text: "white" },
  imageNode: { bg: "#7c3aed", border: "#6d28d9", text: "white" },
  videoNode: { bg: "#db2777", border: "#be185d", text: "white" },
  audioNode: { bg: "#ea580c", border: "#c2410c", text: "white" },
  buttonsNode: { bg: "#0891b2", border: "#0e7490", text: "white" },
  captureNode: { bg: "#06b6d4", border: "#0891b2", text: "white" },
  delayNode: { bg: "#ca8a04", border: "#a16207", text: "white" },
  conditionNode: { bg: "#9333ea", border: "#7e22ce", text: "white" },
  aiNode: { bg: "#059669", border: "#047857", text: "white" },
  actionNode: { bg: "#0369a1", border: "#075985", text: "white" },
  webhookNode: { bg: "#d97706", border: "#b45309", text: "white" },
  prospectaNode: { bg: "#7c3aed", border: "#6d28d9", text: "white" },
  endNode: { bg: "#dc2626", border: "#b91c1c", text: "white" },
};

const actionTypes = [
  { value: "adicionar_etiqueta", label: "Adicionar etiqueta" },
  { value: "remover_etiqueta", label: "Remover etiqueta" },
  { value: "transferir_atendimento", label: "Transferir atendimento" },
  { value: "mover_status", label: "Mover para aba/status" },
  { value: "finalizar_atendimento", label: "Finalizar atendimento" },
  { value: "pausa_robo", label: "Pausa Robo" },
  { value: "agendar_mensagem", label: "Agendar mensagem/envio automatico" },
  { value: "ir_para_flow", label: "Ir para outro fluxo (conectar)" },
];

const statusOptions = [
  { value: "ABERTO", label: "Aguardando", color: "#f59e0b" },
  { value: "SIMULACAO_BANCARIA", label: "Simulacao Bancaria", color: "#0284c7" },
  { value: "EM_ATENDIMENTO", label: "Em Atendimento", color: "#3b82f6" },
  { value: "NEGOCIACAO", label: "Negociacao", color: "#8b5cf6" },
  { value: "ANALISE_BANCO", label: "Proposta em Analise", color: "#06b6d4" },
  { value: "AGENDADO_MSG", label: "Agendados Msg", color: "#8b5cf6" },
  { value: "ATENDIMENTO_IA", label: "Atendimento IA", color: "#059669" },
  { value: "PAUSADO_ROBO", label: "Pausado Robo", color: "#6b7280" },
  { value: "FINALIZADO", label: "Finalizado", color: "#22c55e" },
];

function BaseNode({ data, type, children, handles }) {
  const c = nodeColors[type] || nodeColors.messageNode;
  const icons = { startNode: Play, messageNode: MessageCircle, imageNode: Image, videoNode: Video,
    audioNode: Mic, buttonsNode: MousePointerClick, delayNode: Clock, conditionNode: GitBranch,
    aiNode: Sparkles, actionNode: Zap, prospectaNode: Briefcase, endNode: Square };
  const Icon = icons[type] || MessageCircle;
  return (
    <div className="rounded-lg shadow-lg border-2 min-w-[220px] max-w-[280px] group relative" style={{ borderColor: c.border, background: "#1e1e2e" }}>
      {type !== "startNode" && data.onDeleteNode && (
        <button onClick={(e) => { e.stopPropagation(); data.onDeleteNode(); }} className="absolute -top-2.5 -right-2.5 h-6 w-6 flex items-center justify-center rounded-full bg-red-600 hover:bg-red-500 text-white shadow-lg z-10 border-2 border-red-900">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-t-md" style={{ background: c.bg }}>
        <Icon className="h-3.5 w-3.5" style={{ color: c.text }} />
        <span className="text-[11px] font-bold truncate flex-1" style={{ color: c.text }}>{data.label || type}</span>
      </div>
      <div className="px-2.5 py-2 text-[10px] text-gray-300">{children}</div>
      {type !== "startNode" && <Handle type="target" position={Position.Top} className="!bg-gray-400 !w-2.5 !h-2.5 !border-2 !border-gray-700" />}
      {handles !== null && (handles || (type !== "endNode" && <Handle type="source" position={Position.Bottom} className="!bg-green-400 !w-2.5 !h-2.5 !border-2 !border-gray-700" />))}
    </div>
  );
}

function StartNode({ data }) {
  return <BaseNode data={data} type="startNode"><p className="text-gray-400">Inicio do fluxo</p></BaseNode>;
}
function MessageNode({ data }) {
  return <BaseNode data={data} type="messageNode"><p className="line-clamp-4 whitespace-pre-line">{data.message || "Mensagem..."}</p></BaseNode>;
}
function ImageNode({ data }) {
  return <BaseNode data={data} type="imageNode">
    {data.url ? <div className="space-y-0.5"><p className="text-green-400 text-[9px] truncate">{data.filename || "Imagem"}</p>{data.caption && <p className="text-gray-400 truncate">{data.caption}</p>}</div> : <p className="truncate">Enviar imagem...</p>}
  </BaseNode>;
}
function VideoNode({ data }) {
  return <BaseNode data={data} type="videoNode">
    {data.url ? <div className="space-y-0.5"><p className="text-green-400 text-[9px] truncate">{data.filename || "Video"}</p>{data.caption && <p className="text-gray-400 truncate">{data.caption}</p>}</div> : <p className="truncate">Enviar video...</p>}
  </BaseNode>;
}
function AudioNode({ data }) {
  return <BaseNode data={data} type="audioNode">
    {data.url ? <p className="text-green-400 text-[9px] truncate">{data.filename || "Audio"}</p> : <p className="truncate">Enviar audio...</p>}
  </BaseNode>;
}
function ButtonsNode({ data }) {
  const btns = data.buttons || [];
  return (
    <BaseNode data={data} type="buttonsNode" handles={null}>
      <p className="mb-1 whitespace-pre-line">{data.message || "Escolha:"}</p>
      {btns.map((b, i) => (
        <div key={i} className="relative rounded px-2 py-1 mb-1 text-[9px] bg-cyan-900/40 text-cyan-300 pr-5">
          {b.type === "url" ? ">> " : ""}{b.label || `Botao ${i + 1}`}
          <Handle type="source" position={Position.Right} id={`btn-${i}`}
            className="!bg-cyan-400 !w-2.5 !h-2.5 !border-2 !border-gray-700 !right-[-8px]"
            style={{ top: "50%", transform: "translateY(-50%)" }} />
        </div>
      ))}
      {data.inatividade_enabled !== false && (
        <div className="relative rounded px-2 py-1 mt-1 text-[9px] bg-fuchsia-900/40 text-fuchsia-200 pr-5 border border-fuchsia-700/50">
          Inatividade ({data.inatividade_timeout_1 ?? 3}+{data.inatividade_timeout_2 ?? 3}min)
          <Handle type="source" position={Position.Right} id="inatividade"
            className="!bg-fuchsia-400 !w-2.5 !h-2.5 !border-2 !border-gray-700 !right-[-8px]"
            style={{ top: "50%", transform: "translateY(-50%)" }} title="Inatividade (sem resposta)" />
        </div>
      )}
    </BaseNode>
  );
}
function DelayNode({ data }) {
  return <BaseNode data={data} type="delayNode"><p>{data.seconds || 3} segundos</p><Handle id="fallback" type="source" position={Position.Right} className="!bg-amber-500 !w-2.5 !h-2.5 !border-2 !border-amber-700" style={{ top: "auto", bottom: 8 }} title="Fallback (resposta invalida)" />
  </BaseNode>;
}
function ConditionNode({ data }) {
  return (
    <BaseNode data={data} type="conditionNode"
      handles={<><Handle type="source" position={Position.Bottom} id="true" className="!bg-green-400 !w-2 !h-2 !border-2 !border-gray-700" style={{ left: "30%" }} />
        <Handle type="source" position={Position.Bottom} id="false" className="!bg-red-400 !w-2 !h-2 !border-2 !border-gray-700" style={{ left: "70%" }} /></>}>
      <p>{data.variable || "variavel"} {data.operator || "=="} {data.value || "?"}</p>
      <div className="flex justify-between mt-1 text-[8px]"><span className="text-green-400">Sim</span><span className="text-red-400">Nao</span></div>
    </BaseNode>
  );
}
function AiNode({ data }) {
  return <BaseNode data={data} type="aiNode">
    {data.assistant_name ? <p className="text-emerald-300 font-bold text-[9px]">{data.assistant_name}</p> : <p className="line-clamp-2">{data.context || "IA responde..."}</p>}
    <Badge className="mt-1 text-[8px] bg-emerald-800 text-emerald-200">{data.model || "gpt-4o"}</Badge>
  </BaseNode>;
}
function WebhookNode({ data }) {
  return <BaseNode data={data} type="webhookNode">
    <p className="text-amber-300 text-[9px] font-mono truncate">{data.url || "URL..."}</p>
    <Badge className="mt-1 text-[8px] bg-amber-800 text-amber-200">{data.method || "POST"}</Badge>
  </BaseNode>;
}
function ProspectaNode({ data }) {
  const isDigitar = data.mode === "digitar";
  const handles = isDigitar ? (
    <>
      <Handle type="source" position={Position.Bottom} id="sucesso" className="!bg-green-400 !w-2 !h-2 !border-2 !border-gray-700" style={{ left: "30%" }} title="Proposta digitada" />
      <Handle type="source" position={Position.Bottom} id="erro" className="!bg-red-400 !w-2 !h-2 !border-2 !border-gray-700" style={{ left: "70%" }} title="Erro" />
      <Handle type="source" position={Position.Right} id="dados_faltando" className="!bg-amber-400 !w-2 !h-2 !border-2 !border-gray-700" style={{ top: "60%" }} title="Faltam dados bancarios" />
    </>
  ) : (
    <>
      <Handle type="source" position={Position.Bottom} id="com_oferta" className="!bg-green-400 !w-2 !h-2 !border-2 !border-gray-700" style={{ left: "20%" }} title="Com oferta" />
      <Handle type="source" position={Position.Bottom} id="sem_oferta" className="!bg-orange-400 !w-2 !h-2 !border-2 !border-gray-700" style={{ left: "45%" }} title="Sem oferta / sem margem" />
      <Handle type="source" position={Position.Bottom} id="erro" className="!bg-red-400 !w-2 !h-2 !border-2 !border-gray-700" style={{ left: "70%" }} title="Erro tecnico" />
      <Handle type="source" position={Position.Right} id="dados_faltando" className="!bg-amber-400 !w-2 !h-2 !border-2 !border-gray-700" style={{ top: "60%" }} title="Faltam dados (CPF/nascimento/mae)" />
    </>
  );
  return (
    <BaseNode data={data} type="prospectaNode" handles={handles}>
      <p className="text-violet-200 text-[10px] font-bold">{isDigitar ? "Digitar Proposta" : "Simular UY3 / Paypro"}</p>
      <p className="text-gray-400 text-[8px] mt-0.5">{isDigitar ? "Conta do cliente -> proposta" : "Consulta oferta CLT por CPF"}</p>
      <div className="flex justify-between mt-1 text-[8px]">
        {isDigitar
          ? (<><span className="text-green-400">Sucesso</span><span className="text-red-400">Erro</span></>)
          : (<><span className="text-green-400">Oferta</span><span className="text-orange-300">Sem</span><span className="text-red-400">Erro</span></>)}
      </div>
    </BaseNode>
  );
}
function EndNode({ data }) {
  return <BaseNode data={data} type="endNode"><p className="text-gray-400">Fim do fluxo</p></BaseNode>;
}
function ActionNode({ data }) {
  const act = actionTypes.find(a => a.value === data.action);
  return <BaseNode data={data} type="actionNode">
    <p className="text-sky-300 text-[9px] font-medium">{act?.label || "Selecionar acao..."}</p>
    {data.action === "adicionar_etiqueta" && (data.selected_tags || []).length > 0 && <p className="text-green-400 truncate text-[8px] mt-0.5">{(data.selected_tags || []).join(", ")}</p>}
    {data.action === "remover_etiqueta" && <p className="text-red-400 truncate text-[8px] mt-0.5">{data.remove_all ? "Todas" : (data.selected_tags || []).join(", ")}</p>}
    {data.action === "transferir_atendimento" && data.action_value && <p className="text-green-400 truncate text-[8px] mt-0.5">{data.transfer_name || data.action_value}</p>}
    {data.action === "pausa_robo" && <p className="text-amber-400 truncate text-[8px] mt-0.5">Ate reativacao manual</p>}
    {data.action_value && !["adicionar_etiqueta", "remover_etiqueta", "transferir_atendimento", "pausa_robo", "finalizar_atendimento"].includes(data.action) && <p className="text-gray-400 truncate text-[8px] mt-0.5">{data.action_value}</p>}
  </BaseNode>;
}

function CaptureNode({ data }) {
  return <BaseNode data={data} type="captureNode">
    <div className="flex flex-col gap-0.5">
      <p className="font-bold text-[9px] text-cyan-300 uppercase tracking-wider">Capturar Texto</p>
      <p className="line-clamp-2 text-[10px]">{data.message || "Pergunte ao cliente..."}</p>
      <p className="text-[8px] text-emerald-300 mt-0.5">→ var: {`{{${data.variable || "resposta"}}}`}</p>
      {data.validation && data.validation !== "any" && (
        <p className="text-[8px] text-amber-300">Valida: {data.validation.toUpperCase()}</p>
      )}
      {data.inatividade_enabled !== false && (
        <p className="text-[8px] text-fuchsia-300 mt-0.5">Inatividade: {data.inatividade_timeout_1 ?? 3}+{data.inatividade_timeout_2 ?? 3}min</p>
      )}
    </div>
    {data.inatividade_enabled !== false && (
      <Handle type="source" position={Position.Right} id="inatividade"
        className="!bg-fuchsia-400 !w-2.5 !h-2.5 !border-2 !border-gray-700"
        style={{ top: "auto", bottom: 8 }} title="Inatividade (sem resposta)" />
    )}
  </BaseNode>;
}

const nodeTypes = { startNode: StartNode, messageNode: MessageNode, imageNode: ImageNode, videoNode: VideoNode,
  audioNode: AudioNode, buttonsNode: ButtonsNode, captureNode: CaptureNode, delayNode: DelayNode, conditionNode: ConditionNode, aiNode: AiNode, actionNode: ActionNode, webhookNode: WebhookNode, prospectaNode: ProspectaNode, endNode: EndNode };

const blockPalette = [
  { type: "startNode", label: "Inicio", Icon: Play, color: "#16a34a" },
  { type: "messageNode", label: "Mensagem", Icon: MessageCircle, color: "#2563eb" },
  { type: "imageNode", label: "Imagem", Icon: Image, color: "#7c3aed" },
  { type: "videoNode", label: "Video", Icon: Video, color: "#db2777" },
  { type: "audioNode", label: "Audio", Icon: Mic, color: "#ea580c" },
  { type: "buttonsNode", label: "Botoes", Icon: MousePointerClick, color: "#0891b2" },
  { type: "captureNode", label: "Capturar Texto", Icon: MessageCircle, color: "#06b6d4" },
  { type: "delayNode", label: "Delay", Icon: Clock, color: "#ca8a04" },
  { type: "conditionNode", label: "Condicao", Icon: GitBranch, color: "#9333ea" },
  { type: "aiNode", label: "IA (GPT)", Icon: Sparkles, color: "#059669" },
  { type: "webhookNode", label: "Webhook", Icon: Zap, color: "#d97706" },
  { type: "actionNode", label: "Acao", Icon: Settings, color: "#0369a1" },
  { type: "prospectaNode", label: "Prospecta CLT (UY3)", Icon: Briefcase, color: "#7c3aed" },
  { type: "endNode", label: "Fim", Icon: Square, color: "#dc2626" },
];

const defaultNodeData = {
  startNode: { label: "Inicio" },
  messageNode: { label: "Mensagem", message: "" },
  imageNode: { label: "Imagem", url: "", caption: "" },
  videoNode: { label: "Video", url: "", caption: "" },
  audioNode: { label: "Audio", url: "" },
  buttonsNode: { label: "Botoes", message: "", footer: "", buttons: [{ label: "Opcao 1", type: "reply" }, { label: "Opcao 2", type: "reply" }], fallback_message: "Nao entendi sua resposta. Por favor, responda com o *numero* ou o *nome* de uma das opcoes acima.", fallback_audio_url: "", fallback_retries: 1, fallback_action: "handoff", inatividade_enabled: true, inatividade_timeout_1: 3, inatividade_timeout_2: 3, inatividade_message: "Voce ainda esta ai? Se ainda precisar de ajuda, e so responder aqui.", inatividade_audio_url: "" },
  captureNode: { label: "Capturar Texto", message: "Por favor, informe seu CPF:", variable: "cpf", validation: "cpf", retry_message: "Formato invalido. Tente novamente:", handoff_message: "Nao consegui validar sua resposta. Um atendente vai te atender.", max_retries: 2, inatividade_enabled: true, inatividade_timeout_1: 3, inatividade_timeout_2: 3, inatividade_message: "Voce ainda esta ai? Se ainda precisar de ajuda, e so responder aqui.", inatividade_audio_url: "" },
  delayNode: { label: "Delay", seconds: 3 },
  conditionNode: { label: "Condicao", variable: "input", operator: "contains", value: "" },
  aiNode: { label: "IA", context: "", model: "gpt-4o", api_key: "", assistant_id: "", assistant_name: "" },
  webhookNode: { label: "Webhook", url: "", method: "POST", headers: "{}", body: "{}", wait_response: false },
  actionNode: { label: "Acao", action: "", action_value: "" },
  prospectaNode: { label: "Prospecta CLT (UY3)", mode: "simular", banks: ["uy3"], usar_nova_vida: true, pre_consulta_msg: "\u23F3 Perfeito, {nome}! Estou consultando seu crédito agora.\nIsso leva alguns segundos, aguarde um instante... \U0001F50D" },
  endNode: { label: "Fim" },
};

// ===== PROPERTIES PANEL =====
function PropertiesPanel({ node, onChange, onDelete, onDuplicate, tags, filas, operators, assistants, flows, flowId }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const inatividadeFileRef = useRef(null);
  const fallbackFileRef = useRef(null);

  if (!node) return (
    <div className="flex flex-col items-center justify-center h-full text-gray-500 text-xs px-4">
      <Settings className="h-8 w-8 mb-2 opacity-30" />
      <p>Selecione um bloco para editar</p>
    </div>
  );
  const d = node.data;
  const update = (key, val) => onChange(node.id, { ...d, [key]: val });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await axios.post(`${API}/wpp/flows/upload`, formData);
      update("url", r.data.url);
      update("filename", r.data.filename);
      toast.success(`Arquivo "${r.data.filename}" enviado!`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro no upload"); }
    setUploading(false);
    e.target.value = "";
  };

  const handleUploadInatividade = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await axios.post(`${API}/wpp/flows/upload`, formData);
      update("inatividade_audio_url", r.data.url);
      toast.success(`Audio "${r.data.filename}" enviado!`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro no upload"); }
    setUploading(false);
    e.target.value = "";
  };

  const handleUploadFallback = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const r = await axios.post(`${API}/wpp/flows/upload`, formData);
      update("fallback_audio_url", r.data.url);
      toast.success(`Audio "${r.data.filename}" enviado!`);
    } catch (err) { toast.error(err.response?.data?.detail || "Erro no upload"); }
    setUploading(false);
    e.target.value = "";
  };

  const MediaUploadSection = ({ accept, label }) => (
    <div className="space-y-2">
      <Label className="text-[10px] text-gray-400">{label}</Label>
      {d.url ? (
        <div className="bg-gray-800 border border-gray-700 rounded-md p-2">
          <div className="flex items-center gap-2 mb-1.5">
            <div className="flex-1 min-w-0">
              <p className="text-[10px] text-green-400 font-medium truncate">{d.filename || d.url}</p>
            </div>
            <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-400 shrink-0" onClick={() => { update("url", ""); update("filename", ""); }}><X className="h-3 w-3" /></Button>
          </div>
          {node.type === "imageNode" && d.url && <img src={d.url.startsWith("/") ? `${window.location.origin}${d.url}` : d.url} alt="" className="w-full h-20 object-cover rounded" />}
          {node.type === "audioNode" && d.url && <audio src={d.url.startsWith("/") ? `${window.location.origin}${d.url}` : d.url} controls className="w-full h-7" />}
          {node.type === "videoNode" && d.url && <video src={d.url.startsWith("/") ? `${window.location.origin}${d.url}` : d.url} controls className="w-full h-20 rounded" />}
        </div>
      ) : (
        <div className="space-y-1.5">
          <button onClick={() => fileRef.current?.click()} disabled={uploading}
            className="w-full flex items-center justify-center gap-1.5 py-3 border-2 border-dashed border-gray-700 rounded-lg text-gray-400 hover:border-green-600 hover:text-green-400 transition-colors text-[10px]">
            <Upload className="h-4 w-4" />
            {uploading ? "Enviando..." : "Enviar Arquivo"}
          </button>
          <input ref={fileRef} type="file" accept={accept} className="hidden" onChange={handleUpload} />
          <div className="flex items-center gap-2"><div className="flex-1 h-px bg-gray-700" /><span className="text-[9px] text-gray-600">ou</span><div className="flex-1 h-px bg-gray-700" /></div>
          <Input className="h-7 text-xs bg-gray-800 border-gray-700 text-white" value={d.url || ""} onChange={e => update("url", e.target.value)} placeholder="URL: https://..." />
        </div>
      )}
    </div>
  );

  const inatividadeSection = (
    <div className="border-t border-fuchsia-800/40 pt-3 mt-2">
      <p className="text-[11px] font-bold text-fuchsia-400 uppercase tracking-wider mb-3">Inatividade (sem resposta)</p>
      <div className="flex items-center justify-between mb-3">
        <Label className="text-xs text-gray-300">Ativar inatividade</Label>
        <input type="checkbox" checked={d.inatividade_enabled !== false} onChange={e => update("inatividade_enabled", e.target.checked)} data-testid="inatividade-enabled" />
      </div>
      {d.inatividade_enabled !== false && (<>
        {/* 1a Etapa - TEXTO */}
        <div className="border border-gray-700 rounded-lg p-3 mb-3 bg-gray-800/40 space-y-2">
          <p className="text-[10px] font-bold text-fuchsia-300 uppercase tracking-wider">1a Etapa - Mensagem de Texto</p>
          <div>
            <Label className="text-xs text-gray-300">Mensagem de orientacao</Label>
            <Textarea value={d.inatividade_message || ""} onChange={e => update("inatividade_message", e.target.value)} rows={2} placeholder="Voce ainda esta ai?..." className="text-sm bg-gray-900 border-gray-700 text-white" data-testid="inatividade-message" />
          </div>
          <div>
            <Label className="text-xs text-gray-300">Aguardar antes de enviar (minutos)</Label>
            <Input type="number" min="1" max="120" value={d.inatividade_timeout_1 ?? 3} onChange={e => update("inatividade_timeout_1", parseInt(e.target.value) || 1)} className="h-9 text-sm bg-gray-900 border-gray-700 text-white" data-testid="inatividade-timeout-1" />
            <p className="text-[10px] text-gray-500 mt-1">Tempo sem resposta do cliente ate enviar este texto de orientacao</p>
          </div>
        </div>

        {/* 2a Etapa - AUDIO */}
        <div className="border border-gray-700 rounded-lg p-3 mb-3 bg-gray-800/40 space-y-2">
          <p className="text-[10px] font-bold text-fuchsia-300 uppercase tracking-wider">2a Etapa - Audio</p>
          <div>
            <Label className="text-xs text-gray-300">Audio (opcional)</Label>
            {d.inatividade_audio_url ? (
              <div className="bg-gray-800 border border-gray-700 rounded-md p-2 flex items-center gap-2">
                <audio src={d.inatividade_audio_url.startsWith("/") ? `${window.location.origin}${d.inatividade_audio_url}` : d.inatividade_audio_url} controls className="w-full h-7" />
                <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-400 shrink-0" onClick={() => update("inatividade_audio_url", "")} data-testid="inatividade-audio-remove"><X className="h-3 w-3" /></Button>
              </div>
            ) : (
              <div className="space-y-1.5">
                <button onClick={() => inatividadeFileRef.current?.click()} disabled={uploading} className="w-full flex items-center justify-center gap-1.5 py-2 border-2 border-dashed border-gray-700 rounded-lg text-gray-400 hover:border-fuchsia-600 hover:text-fuchsia-400 transition-colors text-[10px]" data-testid="inatividade-audio-upload"><Upload className="h-4 w-4" />{uploading ? "Enviando..." : "Enviar Audio"}</button>
                <input ref={inatividadeFileRef} type="file" accept="audio/*" className="hidden" onChange={handleUploadInatividade} />
                <Input className="h-7 text-xs bg-gray-800 border-gray-700 text-white" value={d.inatividade_audio_url || ""} onChange={e => update("inatividade_audio_url", e.target.value)} placeholder="URL do audio: https://..." data-testid="inatividade-audio" />
              </div>
            )}
          </div>
          <div>
            <Label className="text-xs text-gray-300">Aguardar antes de enviar (minutos)</Label>
            <Input type="number" min="1" max="120" value={d.inatividade_timeout_2 ?? 3} onChange={e => update("inatividade_timeout_2", parseInt(e.target.value) || 1)} className="h-9 text-sm bg-gray-900 border-gray-700 text-white" data-testid="inatividade-timeout-2" />
            <p className="text-[10px] text-gray-500 mt-1">Tempo apos a 1a etapa (texto) ate enviar o audio</p>
          </div>
        </div>

        <p className="text-[10px] text-fuchsia-400 italic mt-1">Apos o audio: segue a porta ROXA "Inatividade" (se conectada a outro no) ou transfere para atendente.</p>
      </>)}
    </div>
  );

  return (
    <div className="p-3 space-y-3 text-xs">
      <div className="flex items-center justify-between pr-8">
        <span className="font-bold text-gray-200 text-sm">{d.label}</span>
        <div className="flex items-center gap-1">
          {onDuplicate && node.type !== "startNode" && (
            <Button size="sm" variant="ghost" className="h-8 px-2 gap-1 text-blue-400 hover:text-blue-300 hover:bg-blue-900/30" onClick={() => onDuplicate(node.id)} data-testid="btn-duplicar-bloco"><Copy className="h-4 w-4" /><span className="text-[10px]">Duplicar</span></Button>
          )}
          <Button size="sm" variant="ghost" className="h-8 px-2 gap-1 text-red-400 hover:text-red-300 hover:bg-red-900/30" onClick={() => onDelete(node.id)}><Trash2 className="h-4 w-4" /><span className="text-[10px]">Excluir</span></Button>
        </div>
      </div>
      <div><Label className="text-xs text-gray-400">Nome</Label><Input className="h-9 text-sm bg-gray-800 border-gray-700 text-white" value={d.label || ""} onChange={e => update("label", e.target.value)} /></div>

      {(node.type === "messageNode") && (
        <div><Label className="text-xs text-gray-400">Mensagem</Label><Textarea className="text-xs bg-gray-800 border-gray-700 text-white h-40 whitespace-pre-wrap" value={d.message || ""} onChange={e => update("message", e.target.value)} placeholder="Digite a mensagem..." /></div>
      )}
      {node.type === "imageNode" && (<>
        <MediaUploadSection accept="image/*" label="Imagem" />
        <div><Label className="text-xs text-gray-400">Legenda</Label><Input className="h-7 text-xs bg-gray-800 border-gray-700 text-white" value={d.caption || ""} onChange={e => update("caption", e.target.value)} /></div>
      </>)}
      {node.type === "videoNode" && (<>
        <MediaUploadSection accept="video/*" label="Video" />
        <div><Label className="text-xs text-gray-400">Legenda</Label><Input className="h-7 text-xs bg-gray-800 border-gray-700 text-white" value={d.caption || ""} onChange={e => update("caption", e.target.value)} /></div>
      </>)}
      {node.type === "audioNode" && (
        <MediaUploadSection accept="audio/*" label="Audio" />
      )}
      {node.type === "captureNode" && (<>
        <div>
          <Label className="text-xs">Mensagem (pergunta para o cliente)</Label>
          <Textarea value={node.data.message || ""} onChange={(e) => update("message", e.target.value)} placeholder="Ex: Por favor, informe seu CPF (apenas numeros)" rows={3} className="text-sm bg-gray-900 border-gray-700 text-white" data-testid="capture-message" />
          <p className="text-[10px] text-gray-500 mt-1">Use {`{nome}`} para personalizar</p>
        </div>
        <div className="space-y-2">
          <div>
            <Label className="text-xs text-gray-300">Nome da Variavel</Label>
            <Input value={node.data.variable || ""} onChange={(e) => update("variable", e.target.value.replace(/[^a-zA-Z0-9_]/g, ""))} placeholder="cpf" className="text-sm bg-gray-900 border-gray-700 text-white" data-testid="capture-variable" />
            <p className="text-[10px] text-gray-500 mt-1">Use depois como {`{{${node.data.variable || "var"}}}`}</p>
          </div>
          <div>
            <Label className="text-xs text-gray-300">Validar Como</Label>
            <Select value={node.data.validation || "any"} onValueChange={(v) => update("validation", v)}>
              <SelectTrigger className="text-sm bg-gray-900 border-gray-700 text-white" data-testid="capture-validation"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="any">Qualquer texto</SelectItem>
                <SelectItem value="cpf">CPF (11 digitos)</SelectItem>
                <SelectItem value="date">Data (dd/mm/aaaa)</SelectItem>
                <SelectItem value="phone">Telefone</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="number">Numero</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Mensagem em caso de Formato Invalido</Label>
          <Input value={node.data.retry_message || ""} onChange={(e) => update("retry_message", e.target.value)} placeholder="Formato invalido. Tente novamente:" className="text-sm bg-gray-900 border-gray-700 text-white" data-testid="capture-retry-msg" />
        </div>
        <div className="space-y-2">
          <div>
            <Label className="text-xs text-gray-300">Tentativas Maximas</Label>
            <Input type="number" min="1" max="5" value={node.data.max_retries ?? 2} onChange={(e) => update("max_retries", parseInt(e.target.value) || 2)} className="text-sm bg-gray-900 border-gray-700 text-white" data-testid="capture-max-retries" />
          </div>
          <div>
            <Label className="text-xs text-gray-300">Mensagem antes de transferir (apos esgotar)</Label>
            <Input value={node.data.handoff_message || ""} onChange={(e) => update("handoff_message", e.target.value)} placeholder="Mensagem antes de transferir" className="text-sm bg-gray-900 border-gray-700 text-white" data-testid="capture-handoff-msg" />
          </div>
        </div>
        {inatividadeSection}
      </>)}
      
      {node.type === "buttonsNode" && (<>
        <div><Label className="text-xs text-gray-400">Mensagem</Label><Textarea className="text-xs bg-gray-800 border-gray-700 text-white h-36 whitespace-pre-wrap" value={d.message || ""} onChange={e => update("message", e.target.value)} /></div>
        <div><Label className="text-xs text-gray-400">Rodape (opcional)</Label><Input className="h-9 text-sm bg-gray-800 border-gray-700 text-white" value={d.footer || ""} onChange={e => update("footer", e.target.value)} placeholder="Texto do rodape..." /></div>
        <div><Label className="text-xs text-gray-400">Botoes</Label>
          {(d.buttons || []).map((b, i) => (
            <div key={i} className="border border-gray-700 rounded-lg p-3 mb-3 bg-gray-800/50 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-500 font-mono">#{i+1}</span>
                  <select className="h-8 text-xs bg-gray-800 border border-gray-700 text-white rounded px-2" value={b.type || "reply"} onChange={e => {
                    const btns = [...(d.buttons || [])]; btns[i] = { ...btns[i], type: e.target.value }; update("buttons", btns);
                  }}>
                    <option value="reply">Resposta</option>
                    <option value="url">URL</option>
                  </select>
                </div>
                <Button size="sm" variant="ghost" className="h-7 px-2 gap-1 text-red-400 hover:text-red-300 hover:bg-red-900/30 text-[10px]" onClick={() => {
                  const btns = (d.buttons || []).filter((_, j) => j !== i); update("buttons", btns);
                }}><X className="h-3.5 w-3.5" /> Remover</Button>
              </div>
              <Input className="h-9 text-sm bg-gray-800 border-gray-700 text-white w-full" value={b.label} placeholder="Texto do botao..." onChange={e => {
                const btns = [...(d.buttons || [])]; btns[i] = { ...btns[i], label: e.target.value }; update("buttons", btns);
              }} />
              {(b.type === "url") && (
                <Input className="h-9 text-xs bg-gray-900 border-gray-600 text-amber-300 font-mono w-full" value={b.url || ""} placeholder="https://..." onChange={e => {
                  const btns = [...(d.buttons || [])]; btns[i] = { ...btns[i], url: e.target.value }; update("buttons", btns);
                }} />
              )}
            </div>
          ))}
          <div className="flex gap-1">
            <Button size="sm" variant="outline" className="h-6 text-[10px] flex-1 border-gray-700 text-gray-300" onClick={() => update("buttons", [...(d.buttons || []), { label: `Opcao ${(d.buttons || []).length + 1}`, type: "reply" }])}><Plus className="h-3 w-3 mr-1" /> Resposta</Button>
            <Button size="sm" variant="outline" className="h-6 text-[10px] flex-1 border-amber-700 text-amber-300" onClick={() => update("buttons", [...(d.buttons || []), { label: "Acessar", type: "url", url: "" }])}><Plus className="h-3 w-3 mr-1" /> URL</Button>
          </div>
        </div>
        <div className="border-t border-gray-700 pt-3 mt-2">
          <p className="text-[11px] font-bold text-amber-500 uppercase tracking-wider mb-3">Resposta Invalida (Fallback)</p>

          {/* 1a Tentativa - TEXTO */}
          <div className="border border-gray-700 rounded-lg p-3 mb-3 bg-gray-800/40 space-y-2">
            <p className="text-[10px] font-bold text-amber-300 uppercase tracking-wider">1a Tentativa - Mensagem de Texto</p>
            <div>
              <Label className="text-xs text-gray-300">Mensagem ao receber resposta invalida</Label>
              <Textarea value={node.data.fallback_message || ""} onChange={(e) => update("fallback_message", e.target.value)} placeholder="Nao entendi sua resposta..." rows={2} className="text-sm bg-gray-900 border-gray-700 text-white" data-testid="btn-fallback-msg" />
            </div>
            <div>
              <Label className="text-xs text-gray-300">Aguardar antes de enviar (segundos)</Label>
              <Input type="number" min="0" max="120" value={node.data.fallback_delay_1 ?? 0} onChange={(e) => update("fallback_delay_1", parseInt(e.target.value) || 0)} className="h-9 text-sm bg-gray-900 border-gray-700 text-white" data-testid="btn-fallback-delay-1" />
              <p className="text-[10px] text-gray-500 mt-1">Tempo de espera apos resposta invalida ate enviar este texto</p>
            </div>
          </div>

          {/* 2a Tentativa - AUDIO */}
          <div className="border border-gray-700 rounded-lg p-3 mb-3 bg-gray-800/40 space-y-2">
            <p className="text-[10px] font-bold text-amber-300 uppercase tracking-wider">2a Tentativa - Audio</p>
            <div>
              <Label className="text-xs text-gray-300">Audio (opcional)</Label>
              {node.data.fallback_audio_url ? (
                <div className="bg-gray-800 border border-gray-700 rounded-md p-2 flex items-center gap-2">
                  <audio src={node.data.fallback_audio_url.startsWith("/") ? `${window.location.origin}${node.data.fallback_audio_url}` : node.data.fallback_audio_url} controls className="w-full h-7" />
                  <Button size="sm" variant="ghost" className="h-5 w-5 p-0 text-red-400 shrink-0" onClick={() => update("fallback_audio_url", "")} data-testid="btn-fallback-audio-remove"><X className="h-3 w-3" /></Button>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <button onClick={() => fallbackFileRef.current?.click()} disabled={uploading} className="w-full flex items-center justify-center gap-1.5 py-2 border-2 border-dashed border-gray-700 rounded-lg text-gray-400 hover:border-amber-600 hover:text-amber-400 transition-colors text-[10px]" data-testid="btn-fallback-audio-upload"><Upload className="h-4 w-4" />{uploading ? "Enviando..." : "Enviar Audio"}</button>
                  <input ref={fallbackFileRef} type="file" accept="audio/*" className="hidden" onChange={handleUploadFallback} />
                  <Input value={node.data.fallback_audio_url || ""} onChange={(e) => update("fallback_audio_url", e.target.value)} placeholder="https://... ou /uploads/audio.ogg" className="h-9 text-sm bg-gray-900 border-gray-700 text-white" data-testid="btn-fallback-audio" />
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs text-gray-300">Aguardar antes de enviar (segundos)</Label>
              <Input type="number" min="0" max="300" value={node.data.fallback_delay_2 ?? 0} onChange={(e) => update("fallback_delay_2", parseInt(e.target.value) || 0)} className="h-9 text-sm bg-gray-900 border-gray-700 text-white" data-testid="btn-fallback-delay-2" />
              <p className="text-[10px] text-gray-500 mt-1">Tempo de espera apos a 1a tentativa ate enviar o audio</p>
            </div>
          </div>

          <div className="space-y-2 mt-2">
            <div>
              <Label className="text-xs text-gray-300">Tentativas antes de transferir</Label>
              <Input type="number" min="0" max="5" value={node.data.fallback_retries ?? 0} onChange={(e) => update("fallback_retries", parseInt(e.target.value) || 0)} className="h-9 text-sm bg-gray-900 border-gray-700 text-white" data-testid="btn-fallback-retries" />
              <p className="text-[10px] text-gray-500 mt-1">0 = transferir direto sem tentativas</p>
            </div>
            <div>
              <Label className="text-xs text-gray-300">Apos esgotar tentativas</Label>
              <Select value={node.data.fallback_action || "handoff"} onValueChange={(v) => update("fallback_action", v)}>
                <SelectTrigger className="h-9 text-sm bg-gray-900 border-gray-700 text-white" data-testid="btn-fallback-action"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="handoff">Transferir para atendente</SelectItem>
                  <SelectItem value="restart">Voltar ao inicio do fluxo</SelectItem>
                  <SelectItem value="endflow">Encerrar fluxo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-[10px] text-amber-400 italic mt-1">Dica: arraste uma seta do handle AMBAR (lateral direita do bloco Botoes) para outro no para criar um caminho de fallback visual!</p>
          </div>
        </div>
        {inatividadeSection}
      </>)}
      {node.type === "delayNode" && (
        <div><Label className="text-[10px] text-gray-400">Segundos</Label><Input type="number" min={1} className="h-7 text-xs bg-gray-800 border-gray-700 text-white" value={d.seconds || 3} onChange={e => update("seconds", parseInt(e.target.value) || 1)} /></div>
      )}
      {node.type === "conditionNode" && (<>
        <div><Label className="text-xs text-gray-400">Variavel</Label>
          <Select value={d.variable || "input"} onValueChange={v => {
            const patch = { variable: v };
            if (v === "tag") { patch.operator = "has_tag"; patch.value = ""; }
            else if (d.operator === "has_tag" || d.operator === "not_has_tag" || d.operator === "no_tags") { patch.operator = "contains"; patch.selected_tags = []; patch.selected_condition_tags = []; patch.value = ""; }
            onChange(node.id, { ...d, ...patch });
          }}>
            <SelectTrigger className="h-9 text-sm bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="input">input (texto digitado)</SelectItem>
              <SelectItem value="tag">tag (WhatsApp)</SelectItem>
              <SelectItem value="cpf">cpf</SelectItem>
              <SelectItem value="nome">nome</SelectItem>
              <SelectItem value="telefone">telefone</SelectItem>
              <SelectItem value="instituicao">instituicao</SelectItem>
              <SelectItem value="cliente">cliente (cadastrado)</SelectItem>
              <SelectItem value="contato">contato (existe)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs text-gray-400">Operador</Label>
          <Select value={d.operator || "contains"} onValueChange={v => update("operator", v)}>
            <SelectTrigger className="h-9 text-sm bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="equals">Igual a</SelectItem>
              <SelectItem value="contains">Contem</SelectItem>
              <SelectItem value="not_empty">Nao vazio</SelectItem>
              <SelectItem value="has_tag">Tem tag</SelectItem>
              <SelectItem value="not_has_tag">Exceto (nao tem a tag)</SelectItem>
              <SelectItem value="no_tags">Sem tag (nao tem nenhuma)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {((d.variable === "tag" || d.operator === "has_tag" || d.operator === "not_has_tag") && d.operator !== "no_tags") ? (
          <div><Label className="text-xs text-gray-400">Selecionar Tags</Label>
            {(d.selected_tags || d.selected_condition_tags || (d.value ? d.value.split(",").map(s=>s.trim()).filter(Boolean) : [])).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1 mb-1">
                {(d.selected_tags || d.selected_condition_tags || (d.value ? d.value.split(",").map(s=>s.trim()).filter(Boolean) : [])).map(tn => {
                  const tObj = tags.find(x => x.nome === tn);
                  return (
                    <span key={tn} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full text-white" style={{ background: tObj?.cor || "#7c3aed" }}>
                      {tn}
                      <button onClick={() => {
                        const curr = d.selected_tags || d.selected_condition_tags || (d.value ? d.value.split(",").map(s=>s.trim()).filter(Boolean) : []);
                        const next = curr.filter(x => x !== tn);
                        onChange(node.id, { ...d, selected_tags: next, selected_condition_tags: next, value: next.join(",") });
                      }} className="hover:text-red-300">x</button>
                    </span>
                  );
                })}
              </div>
            )}
            <Input className="h-8 text-xs bg-gray-800 border-gray-700 text-white mb-1" placeholder="Localizar tag..." value={d._tagSearch || ""} onChange={e => update("_tagSearch", e.target.value)} />
            <div className="space-y-1 max-h-48 overflow-y-auto border border-gray-700 rounded p-2 bg-gray-800">
              {tags.length === 0 ? (
                <p className="text-xs text-gray-500">Nenhuma tag cadastrada</p>
              ) : tags.filter(t => !d._tagSearch || t.nome.toLowerCase().includes((d._tagSearch || "").toLowerCase())).map(t => {
                const currTags = d.selected_tags || d.selected_condition_tags || (d.value ? d.value.split(",").map(s=>s.trim()).filter(Boolean) : []);
                const isSelected = currTags.includes(t.nome);
                return (
                  <button key={t.nome} type="button" onClick={() => {
                    const curr = d.selected_tags || d.selected_condition_tags || (d.value ? d.value.split(",").map(s=>s.trim()).filter(Boolean) : []);
                    const next = isSelected ? curr.filter(x => x !== t.nome) : [...curr, t.nome];
                    onChange(node.id, { ...d, selected_tags: next, selected_condition_tags: next, value: next.join(",") });
                  }}
                    className={`w-full text-left text-xs px-2 py-1.5 rounded flex items-center gap-2 ${isSelected ? 'bg-purple-600 text-white' : 'bg-gray-700/50 text-gray-300 hover:bg-gray-700'}`}>
                    <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: t.cor || "#888" }}></span>
                    {t.nome}
                    {isSelected && <span className="ml-auto text-[10px]">Selecionada</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div><Label className="text-xs text-gray-400">Valor</Label><Input className="h-9 text-sm bg-gray-800 border-gray-700 text-white" value={d.value || ""} onChange={e => update("value", e.target.value)} /></div>
        )}
      </>)}
      {node.type === "aiNode" && (<>
        <div><Label className="text-[10px] text-gray-400">Assistente IA</Label>
          <Select value={d.assistant_id || "_new"} onValueChange={v => {
            if (v === "_new") { onChange(node.id, { ...d, assistant_id: "", assistant_name: "", context: "", model: "gpt-4o" }); }
            else {
              const ast = assistants.find(a => a.id === v);
              if (ast) onChange(node.id, { ...d, assistant_id: ast.id, assistant_name: ast.nome, context: ast.instrucoes, model: ast.modelo });
            }
          }}>
            <SelectTrigger className="h-7 text-xs bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Selecionar assistente..." /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_new">+ Criar novo (personalizado)</SelectItem>
              {assistants.map(a => <SelectItem key={a.id} value={a.id}>{a.nome} ({a.modelo})</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px] text-gray-400">Contexto / Prompt do Sistema</Label><Textarea className="text-xs bg-gray-800 border-gray-700 text-white h-20" value={d.context || ""} onChange={e => update("context", e.target.value)} placeholder="Voce e um assistente..." /></div>
        <div><Label className="text-[10px] text-gray-400">Modelo</Label>
          <Select value={d.model || "gpt-4o"} onValueChange={v => update("model", v)}>
            <SelectTrigger className="h-7 text-xs bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="gpt-4o">GPT-4o</SelectItem><SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem><SelectItem value="gpt-4.1">GPT-4.1</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px] text-gray-400">Chave API (vazio = chave do servidor)</Label><Input className="h-7 text-xs bg-gray-800 border-gray-700 text-white" type="password" value={d.api_key || ""} onChange={e => update("api_key", e.target.value)} placeholder="sk-..." /></div>
      </>)}
      {node.type === "actionNode" && (<>
        <div className="bg-sky-900/20 border border-sky-800/30 rounded-md p-2 text-[10px] text-sky-300">
          Aplique acoes especificas no chatbot, como adicionar etiquetas, transferir para um atendente ou encerrar uma conversa.
        </div>
        <div><Label className="text-[10px] text-gray-400">Acao</Label>
          <Select value={d.action || ""} onValueChange={v => onChange(node.id, { ...d, action: v, action_value: "", selected_tags: [], remove_all: false })}>
            <SelectTrigger className="h-8 text-xs bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Selecionar acao..." /></SelectTrigger>
            <SelectContent>
              {actionTypes.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {d.action && !actionTypes.some(a => a.value === d.action) && (
          <div className="bg-red-950/50 border border-red-700 rounded-md p-2 text-[10px] text-red-200">
            Esta acao antiga nao e executada pelo servidor. Selecione uma acao valida antes de salvar o fluxo.
          </div>
        )}

        {/* ADICIONAR ETIQUETA - mostra tags existentes para selecionar */}
        {d.action === "adicionar_etiqueta" && (
          <div><Label className="text-[10px] text-gray-400">Selecionar Etiqueta</Label>
            {tags.length === 0 ? (
              <p className="text-[10px] text-gray-500 mt-1">Nenhuma tag cadastrada. Crie tags na aba "Tags".</p>
            ) : (
              <div className="space-y-1 mt-1 max-h-[180px] overflow-y-auto">
                {tags.map(t => {
                  const selected = (d.selected_tags || []).includes(t.nome);
                  return (
                    <button key={t.id} onClick={() => {
                      const curr = d.selected_tags || [];
                      const next = selected ? curr.filter(x => x !== t.nome) : [...curr, t.nome];
                      onChange(node.id, { ...d, selected_tags: next, action_value: next.join(", ") });
                    }}
                      className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-[10px] transition-colors ${selected ? "bg-sky-900/40 border border-sky-600" : "bg-gray-800 border border-gray-700 hover:border-gray-600"}`}>
                      <div className="h-3 w-3 rounded-sm border-2 flex items-center justify-center" style={{ borderColor: t.cor, backgroundColor: selected ? t.cor : "transparent" }}>
                        {selected && <Check className="h-2 w-2 text-white" />}
                      </div>
                      <span className="font-bold text-white px-1.5 py-0.5 rounded text-[9px]" style={{ backgroundColor: t.cor }}>{t.nome}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {(d.selected_tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1 mt-1.5">{(d.selected_tags || []).map(tn => {
                const tObj = tags.find(x => x.nome === tn);
                return <span key={tn} className="text-[9px] font-bold px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: tObj?.cor || "#8b5cf6" }}>{tn}</span>;
              })}</div>
            )}
          </div>
        )}

        {/* REMOVER ETIQUETA - todas ou específicas */}
        {d.action === "remover_etiqueta" && (
          <div><Label className="text-[10px] text-gray-400">Remover</Label>
            <div className="space-y-1.5 mt-1">
              <button onClick={() => onChange(node.id, { ...d, remove_all: true, selected_tags: [], action_value: "TODAS" })}
                className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-[10px] transition-colors ${d.remove_all ? "bg-red-900/40 border border-red-600" : "bg-gray-800 border border-gray-700 hover:border-gray-600"}`}>
                <div className={`h-3 w-3 rounded-full border ${d.remove_all ? "bg-red-500 border-red-500" : "border-gray-500"}`} />
                <span className="text-white font-medium">Todas as etiquetas</span>
              </button>
              <button onClick={() => onChange(node.id, { ...d, remove_all: false })}
                className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-[10px] transition-colors ${!d.remove_all ? "bg-sky-900/40 border border-sky-600" : "bg-gray-800 border border-gray-700 hover:border-gray-600"}`}>
                <div className={`h-3 w-3 rounded-full border ${!d.remove_all ? "bg-sky-500 border-sky-500" : "border-gray-500"}`} />
                <span className="text-white font-medium">Etiquetas especificas</span>
              </button>
              {!d.remove_all && tags.length > 0 && (
                <div className="space-y-1 max-h-[140px] overflow-y-auto">
                  {tags.map(t => {
                    const selected = (d.selected_tags || []).includes(t.nome);
                    return (
                      <button key={t.id} onClick={() => {
                        const curr = d.selected_tags || [];
                        const next = selected ? curr.filter(x => x !== t.nome) : [...curr, t.nome];
                        onChange(node.id, { ...d, selected_tags: next, action_value: next.join(", ") });
                      }}
                        className={`flex items-center gap-2 w-full px-2 py-1 rounded text-[10px] transition-colors ${selected ? "bg-red-900/30 border border-red-700" : "bg-gray-800 border border-gray-700 hover:border-gray-600"}`}>
                        <div className="h-3 w-3 rounded-sm border-2 flex items-center justify-center" style={{ borderColor: t.cor, backgroundColor: selected ? t.cor : "transparent" }}>
                          {selected && <X className="h-2 w-2 text-white" />}
                        </div>
                        <span className="font-bold text-white px-1.5 py-0.5 rounded text-[9px]" style={{ backgroundColor: t.cor }}>{t.nome}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TRANSFERIR - filas + operadores */}
        {d.action === "transferir_atendimento" && (
          <div>
            <Label className="text-[10px] text-gray-400">Tipo de Transferencia</Label>
            <div className="flex gap-1 mt-1 mb-2">
              <button onClick={() => onChange(node.id, { ...d, transfer_type: "fila", action_value: "" })}
                className={`flex-1 text-[10px] font-medium py-1.5 rounded text-center transition-colors ${(d.transfer_type || "fila") === "fila" ? "bg-green-700 text-white" : "bg-gray-800 text-gray-400 hover:bg-gray-700"}`}>
                Departamento
              </button>
              <button onClick={() => onChange(node.id, { ...d, transfer_type: "operador", action_value: "" })}
                className={`flex-1 text-[10px] font-medium py-1.5 rounded text-center transition-colors ${d.transfer_type === "operador" ? "bg-blue-700 text-white" : "bg-gray-800 text-gray-400 hover:bg-gray-700"}`}>
                Operador
              </button>
            </div>
            {(d.transfer_type || "fila") === "fila" ? (
              <>
                <Label className="text-[10px] text-gray-400">Departamento / Fila</Label>
                {filas.length === 0 ? (
                  <p className="text-[10px] text-gray-500 mt-1">Nenhuma fila cadastrada.</p>
                ) : (
                  <div className="space-y-1 mt-1">
                    {filas.map(f => {
                      const selected = d.action_value === f.id || d.action_value === f.nome;
                      return <button key={f.id} onClick={() => onChange(node.id, { ...d, action_value: f.id, transfer_name: f.nome })}
                        className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-[10px] transition-colors ${selected ? "bg-green-900/40 border border-green-600" : "bg-gray-800 border border-gray-700 hover:border-gray-600"}`}>
                        <div className={`h-3 w-3 rounded-full border ${selected ? "bg-green-500 border-green-500" : "border-gray-500"}`} />
                        <Users className="h-3 w-3 text-gray-400" />
                        <span className="text-white font-medium">{f.nome}</span>
                      </button>;
                    })}
                  </div>
                )}
              </>
            ) : (
              <>
                <Label className="text-[10px] text-gray-400">Operador</Label>
                {operators.length === 0 ? (
                  <p className="text-[10px] text-gray-500 mt-1">Nenhum operador cadastrado.</p>
                ) : (
                  <div className="space-y-1 mt-1">
                    {operators.map(op => {
                      const operatorName = op.nome || op.email;
                      const selected = d.action_value === op.id || d.action_value === operatorName;
                      return <button key={op.id} onClick={() => onChange(node.id, { ...d, action_value: op.id, transfer_name: operatorName })}
                        className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-[10px] transition-colors ${selected ? "bg-blue-900/40 border border-blue-600" : "bg-gray-800 border border-gray-700 hover:border-gray-600"}`}>
                        <div className={`h-3 w-3 rounded-full border ${selected ? "bg-blue-500 border-blue-500" : "border-gray-500"}`} />
                        <div className="h-5 w-5 rounded-full bg-blue-800 flex items-center justify-center text-[8px] font-bold text-blue-200">{(op.nome || op.email || "?")[0].toUpperCase()}</div>
                        <div className="text-left"><span className="text-white font-medium block">{op.nome || "Sem nome"}</span><span className="text-gray-500 text-[8px]">{op.email}</span></div>
                      </button>;
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* MOVER PARA ABA/STATUS */}
        {d.action === "mover_status" && (
          <div>
            <div className="bg-cyan-900/20 border border-cyan-800/30 rounded-md p-2 mb-2">
              <p className="text-[10px] text-cyan-300">Move o ticket para uma das abas/status do atendimento.</p>
            </div>
            <Label className="text-[10px] text-gray-400">Aba de Destino</Label>
            <div className="space-y-1 mt-1">
              {statusOptions.map(s => (
                <button key={s.value} onClick={() => update("action_value", s.value)}
                  className={`flex items-center gap-2 w-full px-2 py-1.5 rounded text-[10px] transition-colors ${d.action_value === s.value ? "border" : "bg-gray-800 border border-gray-700 hover:border-gray-600"}`}
                  style={d.action_value === s.value ? { backgroundColor: s.color + "30", borderColor: s.color } : {}}
                  data-testid={`btn-status-${s.value}`}>
                  <div className={`h-3 w-3 rounded-full border`} style={d.action_value === s.value ? { backgroundColor: s.color, borderColor: s.color } : { borderColor: "#6b7280" }} />
                  <span className="font-bold" style={{ color: d.action_value === s.value ? s.color : "white" }}>{s.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* FINALIZAR ATENDIMENTO */}
        {d.action === "finalizar_atendimento" && (
          <div className="bg-green-900/20 border border-green-800/30 rounded-md p-2.5">
            <div className="flex items-center gap-2 mb-1"><Check className="h-4 w-4 text-green-400" /><span className="text-[11px] font-bold text-green-300">Finalizar Atendimento</span></div>
            <p className="text-[10px] text-green-400/70">O ticket sera marcado como RESOLVIDO e a conversa sera encerrada.</p>
          </div>
        )}

        {/* PAUSA ROBO - tempo específico */}
        {d.action === "pausa_robo" && (
          <div className="bg-amber-900/20 border border-amber-800/30 rounded-md p-2.5">
              <div className="flex items-center gap-2 mb-1"><Clock className="h-4 w-4 text-amber-400" /><span className="text-[11px] font-bold text-amber-300">Pausa Robo</span></div>
              <p className="text-[10px] text-amber-400/70">Interrompe as respostas automaticas. O robo permanece pausado ate um atendente mover o atendimento para outra fila.</p>
          </div>
        )}
        {d.action === "agendar_mensagem" && (<>
          <div className="bg-emerald-900/20 border border-emerald-800/30 rounded-md p-2 text-[10px] text-emerald-300">
            A mensagem sera enviada automaticamente apos o tempo definido (ou na data/hora exata).
          </div>
          <div><Label className="text-[10px] text-gray-400">Mensagem a Enviar</Label>
            <Textarea className="text-xs bg-gray-800 border-gray-700 text-white h-16" value={d.scheduled_message || ""} onChange={e => update("scheduled_message", e.target.value)} placeholder="Ex: Oi {{nome}}, ainda esta interessado?" />
          </div>
          <div><Label className="text-[10px] text-gray-400">Tipo de Agendamento</Label>
            <Select value={d.schedule_mode || "relative"} onValueChange={v => update("schedule_mode", v)}>
              <SelectTrigger className="h-7 text-xs bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="relative">Relativo (em X tempo)</SelectItem>
                <SelectItem value="absolute">Data/Hora especifica</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {(d.schedule_mode || "relative") === "relative" ? (
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-[10px] text-gray-400">Quantidade</Label>
                <Input type="number" min="1" className="h-7 text-xs bg-gray-800 border-gray-700 text-white" value={d.schedule_amount || 1} onChange={e => update("schedule_amount", parseInt(e.target.value) || 1)} />
              </div>
              <div><Label className="text-[10px] text-gray-400">Unidade</Label>
                <Select value={d.schedule_unit || "minutos"} onValueChange={v => update("schedule_unit", v)}>
                  <SelectTrigger className="h-7 text-xs bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="minutos">Minutos</SelectItem>
                    <SelectItem value="horas">Horas</SelectItem>
                    <SelectItem value="dias">Dias</SelectItem>
                    <SelectItem value="semanas">Semanas</SelectItem>
                    <SelectItem value="meses">Meses</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div><Label className="text-[10px] text-gray-400">Data e Hora</Label>
              <Input type="datetime-local" className="h-7 text-xs bg-gray-800 border-gray-700 text-white" value={d.schedule_datetime || ""} onChange={e => update("schedule_datetime", e.target.value)} />
            </div>
          )}
        </>)}
        {d.action === "ir_para_flow" && (<>
          <div className="bg-purple-900/20 border border-purple-800/30 rounded-md p-2 text-[10px] text-purple-300">
            Executa outro fluxo imediatamente. O fluxo atual sera interrompido.
          </div>
          <div><Label className="text-[10px] text-gray-400">Fluxo de Destino</Label>
            <Select value={d.target_flow_id || ""} onValueChange={v => {
              const f = (flows || []).find(x => x.id === v);
              onChange(node.id, { ...d, target_flow_id: v, target_flow_nome: f?.nome || "" });
            }}>
              <SelectTrigger className="h-8 text-xs bg-gray-800 border-gray-700 text-white"><SelectValue placeholder="Selecione um fluxo..." /></SelectTrigger>
              <SelectContent>
                {(flows || []).filter(f => f.id !== flowId).map(f => (
                  <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {d.target_flow_nome && <p className="text-[10px] text-purple-300 mt-1">→ {d.target_flow_nome}</p>}
          </div>
        </>)}

        {/* === ACOES EXTRAS (encadeamento no mesmo no) === */}
        <div className="border-t border-gray-700 pt-3 mt-3">
          <div className="flex items-center justify-between mb-2">
            <Label className="text-[10px] text-gray-300 font-bold">Acoes Extras (no mesmo bloco)</Label>
            <Button size="sm" variant="outline" className="h-6 text-[10px] gap-1 bg-gray-800 border-gray-700 text-white hover:bg-gray-700" onClick={() => onChange(node.id, { ...d, extra_actions: [...(d.extra_actions || []), { action: "adicionar_etiqueta", action_value: "" }] })} data-testid="btn-add-extra-acao">
              <Plus className="h-3 w-3" /> Adicionar
            </Button>
          </div>
          {(d.extra_actions || []).length === 0 && (
            <p className="text-[10px] text-gray-500 italic">Nenhuma acao extra. As acoes adicionadas aqui rodam logo apos a acao principal.</p>
          )}
          {(d.extra_actions || []).map((ea, idx) => {
            const updateEa = (patch) => {
              const list = [...(d.extra_actions || [])];
              list[idx] = { ...list[idx], ...patch };
              onChange(node.id, { ...d, extra_actions: list });
            };
            const removeEa = () => {
              const list = (d.extra_actions || []).filter((_, i) => i !== idx);
              onChange(node.id, { ...d, extra_actions: list });
            };
            return (
              <div key={idx} className="bg-gray-800/60 border border-gray-700 rounded p-2 mb-2 space-y-1.5" data-testid={`extra-acao-${idx}`}>
                <div className="flex items-center gap-1.5">
                  <span className="text-[9px] text-gray-500 font-bold">#{idx + 1}</span>
                  <Select value={ea.action} onValueChange={v => updateEa({ action: v, action_value: "" })}>
                    <SelectTrigger className="h-6 text-[10px] bg-gray-900 border-gray-700 text-white flex-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="adicionar_etiqueta">Adicionar etiqueta</SelectItem>
                      <SelectItem value="remover_etiqueta">Remover etiqueta</SelectItem>
                      <SelectItem value="mover_status">Mover para aba</SelectItem>
                      <SelectItem value="finalizar_atendimento">Finalizar</SelectItem>
                      <SelectItem value="pausa_robo">Pausar robo</SelectItem>
                      <SelectItem value="transferir_atendimento">Transferir (fila)</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400 hover:text-red-300" onClick={removeEa} data-testid={`btn-rm-extra-${idx}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
                {(ea.action === "adicionar_etiqueta" || ea.action === "remover_etiqueta") && (
                  <Select value={ea.action_value || ""} onValueChange={v => updateEa({ action_value: v })}>
                    <SelectTrigger className="h-6 text-[10px] bg-gray-900 border-gray-700 text-white"><SelectValue placeholder="Selecione tag" /></SelectTrigger>
                    <SelectContent>
                      {tags.map(t => <SelectItem key={t.id} value={t.nome}>{t.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {ea.action === "mover_status" && (
                  <Select value={ea.action_value || ""} onValueChange={v => updateEa({ action_value: v })}>
                    <SelectTrigger className="h-6 text-[10px] bg-gray-900 border-gray-700 text-white"><SelectValue placeholder="Selecione aba" /></SelectTrigger>
                    <SelectContent>
                      {statusOptions.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {ea.action === "transferir_atendimento" && (
                  <Select value={filas.some(f => f.id === ea.action_value) ? ea.action_value : (filas.find(f => f.nome === ea.action_value)?.id || "")} onValueChange={v => {
                    const fila = filas.find(f => f.id === v);
                    updateEa({ action_value: v, transfer_name: fila?.nome || "", transfer_type: "fila" });
                  }}>
                    <SelectTrigger className="h-6 text-[10px] bg-gray-900 border-gray-700 text-white"><SelectValue placeholder="Selecione fila" /></SelectTrigger>
                    <SelectContent>
                      {filas.map(f => <SelectItem key={f.id} value={f.id}>{f.nome}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
                {ea.action === "pausa_robo" && (
                  <p className="text-[9px] text-amber-300">Permanece pausado ate reativacao manual.</p>
                )}
              </div>
            );
          })}
        </div>
      </>)}
      {node.type === "webhookNode" && (<>
        <div className="bg-amber-900/20 border border-amber-800/30 rounded-md p-2 text-[10px] text-amber-300">
          Envia dados para uma URL externa (Webhook, Zapier, API, etc.)
        </div>
        <div><Label className="text-[10px] text-gray-400">URL</Label><Input className="h-7 text-xs bg-gray-800 border-gray-700 text-amber-300 font-mono" value={d.url || ""} onChange={e => update("url", e.target.value)} placeholder="https://hooks.zapier.com/..." /></div>
        <div><Label className="text-[10px] text-gray-400">Metodo HTTP</Label>
          <Select value={d.method || "POST"} onValueChange={v => update("method", v)}>
            <SelectTrigger className="h-7 text-xs bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="POST">POST</SelectItem><SelectItem value="GET">GET</SelectItem><SelectItem value="PUT">PUT</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label className="text-[10px] text-gray-400">Headers (JSON)</Label><Textarea className="text-[10px] bg-gray-800 border-gray-700 text-white h-12 font-mono" value={d.headers || "{}"} onChange={e => update("headers", e.target.value)} placeholder='{"Authorization": "Bearer ..."}' /></div>
        <div><Label className="text-[10px] text-gray-400">Body (JSON) - Use {"{{telefone}}"}, {"{{nome}}"}, {"{{mensagem}}"}</Label><Textarea className="text-[10px] bg-gray-800 border-gray-700 text-white h-16 font-mono" value={d.body || "{}"} onChange={e => update("body", e.target.value)} placeholder='{"phone": "{{telefone}}", "name": "{{nome}}"}' /></div>
        <div className="flex items-center gap-2 mt-1">
          <input type="checkbox" className="rounded" checked={d.wait_response || false} onChange={e => update("wait_response", e.target.checked)} />
          <span className="text-[10px] text-gray-300">Aguardar resposta antes de continuar</span>
        </div>
      </>)}
      {node.type === "prospectaNode" && (<>
        <div className="bg-violet-900/20 border border-violet-800/30 rounded-md p-2 text-[10px] text-violet-200">
          Atendimento automatico Prospecta CLT. O CPF e lido das variaveis do fluxo ({"{{cpf}}"}) ou do ticket (PABX/cadastro). Dados (nome/nascimento/mae) vem do cadastro; se faltarem, tenta Nova Vida e por fim ramifica para <b>Faltam dados</b> (conecte capturas no chat e volte para este no).
        </div>
        <div><Label className="text-[10px] text-gray-400">Etapa</Label>
          <Select value={d.mode || "simular"} onValueChange={v => update("mode", v)}>
            <SelectTrigger className="h-7 text-xs bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="simular">Simular (consulta a oferta UY3)</SelectItem>
              <SelectItem value="digitar">Digitar proposta (apos aceite + conta)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {(d.mode || "simular") === "simular" && (<>
          <div><Label className="text-[10px] text-gray-400">Mensagem "consultando..." (enviada SÓ ao iniciar a consulta real, depois de ter o CPF)</Label>
            <Textarea className="text-[10px] bg-gray-800 border-gray-700 text-white h-16" value={d.pre_consulta_msg || ""} onChange={e => update("pre_consulta_msg", e.target.value)} placeholder="Ex: ⏳ Estou consultando seu crédito, aguarde..." />
          </div>
          <div className="flex items-center gap-2 mt-1">
            <input type="checkbox" className="rounded" checked={d.usar_nova_vida !== false} onChange={e => update("usar_nova_vida", e.target.checked)} />
            <span className="text-[10px] text-gray-300">Buscar dados faltantes via Nova Vida (automatico)</span>
          </div>
          <div className="bg-gray-800/50 rounded p-2 text-[9px] text-gray-400 mt-1 leading-relaxed">
            <b className="text-violet-300">Saidas:</b> <span className="text-green-400">Com oferta</span> / <span className="text-orange-300">Sem oferta</span> / <span className="text-red-400">Erro</span> / <span className="text-amber-300">Faltam dados</span>.<br/>
            <b className="text-violet-300">Variaveis geradas:</b> {"{{prospecta_banco}}"}, {"{{prospecta_valor}}"}, {"{{prospecta_parcela}}"}, {"{{prospecta_parcelas}}"}, {"{{prospecta_motivo}}"}.
          </div>
        </>)}
        {d.mode === "digitar" && (
          <div className="bg-gray-800/50 rounded p-2 text-[9px] text-gray-400 mt-1 leading-relaxed">
            Le os dados bancarios das variaveis: {"{{banco}}"}, {"{{agencia}}"}, {"{{conta}}"}, {"{{conta_digito}}"}, {"{{tipo_conta}}"} (capture-os antes com nos "Capturar Texto").<br/>
            <b className="text-violet-300">Saidas:</b> <span className="text-green-400">Sucesso</span> / <span className="text-red-400">Erro</span> / <span className="text-amber-300">Faltam dados</span>.<br/>
            <b className="text-violet-300">Variaveis geradas:</b> {"{{prospecta_proposta_numero}}"}, {"{{prospecta_link_assinatura}}"}, {"{{prospecta_status}}"}.
          </div>
        )}
      </>)}
    </div>
  );
}

// ===== CHAT SIMULATOR =====
function ChatSimulator({ flow, onClose }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [nodeId, setNodeId] = useState("");
  const [sessionId] = useState(() => "sim-" + Date.now());
  const [loading, setLoading] = useState(false);
  const endRef = useRef(null);
  const autoStartedRef = useRef(false);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const startFlow = async () => {
    setMsgs([]); setNodeId("");
    if (!flow?.id) {
      setMsgs([{ tipo: "sistema", texto: "Salve o fluxo antes de simular." }]);
      return;
    }
    await processNode("", "");
  };

  // Auto-iniciar o simulador na primeira abertura
  useEffect(() => {
    if (!autoStartedRef.current && flow?.id) {
      autoStartedRef.current = true;
      startFlow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const processNode = async (nId, userInput) => {
    if (!flow?.id) {
      setMsgs(p => [...p, { tipo: "sistema", texto: "Salve o fluxo antes de simular." }]);
      return;
    }
    setLoading(true);
    try {
      const r = await axios.post(`${API}/wpp/flows/${flow.id}/simulate`, {
        node_id: nId,
        input: userInput,
        session_id: sessionId,
        nodes: flow.nodes || [],
        edges: flow.edges || []
      });
      const newMsgs = r.data.messages || [];
      setMsgs(p => [...p, ...newMsgs]);
      const next = r.data.next_node;
      if (next && next !== nId) {
        setNodeId(next);
        const nextNode = (flow.nodes || []).find(n => n.id === next);
        if (nextNode && !["buttonsNode", "conditionNode", "aiNode", "prospectaNode"].includes(nextNode.type)) {
          setTimeout(() => processNode(next, ""), 500);
        }
        if (nextNode && nextNode.type === "aiNode") {
          setTimeout(() => processNode(next, userInput || "Ola"), 300);
        }
      } else if (!next || next === null) {
        setNodeId("");
      }
    } catch (e) { setMsgs(p => [...p, { tipo: "sistema", texto: "Erro: " + (e.response?.data?.detail || e.message) }]); }
    setLoading(false);
  };

  const sendMsg = async () => {
    if (!input.trim()) return;
    setMsgs(p => [...p, { tipo: "user", texto: input.trim() }]);
    const txt = input.trim();
    setInput("");
    await processNode(nodeId, txt);
  };

  return (
    <div className="flex flex-col h-full bg-[#0d1117]">
      <div className="flex items-center justify-between px-3 py-2 bg-[#161b22] border-b border-gray-800">
        <div className="flex items-center gap-2"><Bot className="h-4 w-4 text-green-400" /><span className="text-sm font-bold text-white">Simulador</span></div>
        <div className="flex gap-1">
          <Button size="sm" variant="ghost" className="h-6 text-[10px] text-green-400 hover:text-green-300" onClick={startFlow}><RotateCcw className="h-3 w-3 mr-1" /> Reiniciar</Button>
          <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400" onClick={onClose}><X className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {msgs.length === 0 && (
          <div className="text-center py-8 text-gray-500 text-xs">
            <Bot className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>Clique "Reiniciar" para testar</p>
          </div>
        )}
        {msgs.map((m, i) => (
          <div key={i} className={`flex ${m.tipo === "user" ? "justify-end" : m.tipo === "sistema" ? "justify-center" : "justify-start"}`}>
            {m.tipo === "sistema" ? (
              <span className="text-[9px] text-gray-500 bg-gray-800 px-2 py-0.5 rounded-full">{m.texto}</span>
            ) : m.tipo === "action" ? (
              <span className="text-[9px] text-sky-400 bg-sky-900/30 border border-sky-800/30 px-2 py-1 rounded-lg flex items-center gap-1"><Zap className="h-3 w-3" /> {m.texto}</span>
            ) : m.tipo === "webhook" ? (
              <span className="text-[9px] text-amber-400 bg-amber-900/30 border border-amber-800/30 px-2 py-1 rounded-lg flex items-center gap-1"><Zap className="h-3 w-3" /> {m.texto}</span>
            ) : m.tipo === "buttons" ? (
              <div className="flex flex-wrap gap-1">
                {(m.buttons || []).map((b, j) => (
                  b.type === "url" ? (
                    <a key={j} href={b.url || "#"} target="_blank" rel="noopener noreferrer"
                      className="text-[10px] font-bold px-3 py-1 rounded-full bg-amber-700 text-white hover:bg-amber-600 transition-colors inline-flex items-center gap-1">
                      🔗 {b.label}
                    </a>
                  ) : (
                    <button key={j} className="text-[10px] font-bold px-3 py-1 rounded-full bg-cyan-600 text-white hover:bg-cyan-500 transition-colors"
                      onClick={() => { setMsgs(p => [...p, { tipo: "user", texto: b.label }]); processNode(nodeId, b.label); }}>{b.label}</button>
                  )
                ))}
              </div>
            ) : (
              <div className={`max-w-[80%] px-3 py-1.5 rounded-2xl text-xs ${m.tipo === "user" ? "bg-green-700 text-white rounded-tr-sm" : "bg-gray-800 text-gray-200 rounded-tl-sm"}`}>
                {m.texto}
              </div>
            )}
          </div>
        ))}
        {loading && <div className="flex justify-start"><div className="bg-gray-800 text-gray-400 px-3 py-1.5 rounded-2xl rounded-tl-sm text-xs animate-pulse">digitando...</div></div>}
        <div ref={endRef} />
      </div>
      <div className="px-3 py-2 border-t border-gray-800">
        <div className="flex gap-2">
          <Input className="flex-1 h-8 rounded-full bg-gray-800 border-gray-700 text-white text-xs" placeholder="Digite..." value={input}
            onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") sendMsg(); }} />
          <Button size="sm" className="h-8 w-8 p-0 rounded-full bg-green-600 hover:bg-green-700" onClick={sendMsg}><Send className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
    </div>
  );
}

// ===== MAIN FLOW BUILDER =====
// ===== ASSISTANTS MANAGER =====
function AssistantsManager({ assistants, onRefresh }) {
  const [form, setForm] = useState({ nome: "", instrucoes: "", modelo: "gpt-4o" });
  const [editing, setEditing] = useState(null);

  const salvar = async () => {
    if (!form.nome.trim()) return toast.error("Nome obrigatorio");
    try {
      if (editing) {
        await axios.put(`${API}/wpp/assistants/${editing}`, form);
        toast.success("Assistente atualizado!");
      } else {
        await axios.post(`${API}/wpp/assistants`, form);
        toast.success("Assistente criado!");
      }
      setForm({ nome: "", instrucoes: "", modelo: "gpt-4o" });
      setEditing(null);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const excluir = async (id) => {
    if (!window.confirm("Excluir assistente?")) return;
    try { await axios.delete(`${API}/wpp/assistants/${id}`); onRefresh(); toast.success("Excluido!"); } catch { toast.error("Erro"); }
  };

  return (
    <div className="space-y-3">
      <div className="max-h-48 overflow-y-auto space-y-1.5">
        {assistants.length === 0 ? <p className="text-gray-500 text-xs text-center py-3">Nenhum assistente cadastrado</p> : assistants.map(a => (
          <div key={a.id} className="flex items-center justify-between bg-gray-800/50 border border-gray-700 rounded p-2">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-white truncate">{a.nome}</p>
              <p className="text-[9px] text-gray-400 truncate">{a.instrucoes?.substring(0, 60) || "Sem instrucoes"}...</p>
              <Badge className="text-[8px] bg-emerald-900 text-emerald-300 mt-0.5">{a.modelo}</Badge>
            </div>
            <div className="flex gap-1 ml-2 shrink-0">
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400" onClick={() => { setForm({ nome: a.nome, instrucoes: a.instrucoes, modelo: a.modelo }); setEditing(a.id); }}><Pencil className="h-3 w-3" /></Button>
              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-400" onClick={() => excluir(a.id)}><X className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
      </div>
      <div className="border-t border-gray-700 pt-3 space-y-2">
        <p className="text-[10px] font-bold text-gray-400 uppercase">{editing ? "Editar Assistente" : "Novo Assistente"}</p>
        <Input className="h-7 text-xs bg-gray-800 border-gray-700 text-white" value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Nome do assistente" />
        <Select value={form.modelo} onValueChange={v => setForm(p => ({ ...p, modelo: v }))}>
          <SelectTrigger className="h-7 text-xs bg-gray-800 border-gray-700 text-white"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="gpt-4o">GPT-4o</SelectItem><SelectItem value="gpt-4o-mini">GPT-4o Mini</SelectItem><SelectItem value="gpt-4.1">GPT-4.1</SelectItem></SelectContent>
        </Select>
        <Textarea className="text-xs bg-gray-800 border-gray-700 text-white h-20" value={form.instrucoes} onChange={e => setForm(p => ({ ...p, instrucoes: e.target.value }))} placeholder="Instrucoes do assistente... Ex: Voce e um assistente de vendas da empresa X..." />
        <div className="flex gap-2">
          {editing && <Button size="sm" variant="outline" className="flex-1 h-7 text-[10px] border-gray-700 text-gray-300" onClick={() => { setEditing(null); setForm({ nome: "", instrucoes: "", modelo: "gpt-4o" }); }}>Cancelar</Button>}
          <Button size="sm" className="flex-1 h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={salvar}>{editing ? "Atualizar" : "Criar Assistente"}</Button>
        </div>
      </div>
    </div>
  );
}

export default function FlowBuilder({ onBack }) {
  const [flows, setFlows] = useState([]);
  const [currentFlow, setCurrentFlow] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showSimulator, setShowSimulator] = useState(false);
  const [showNewFlow, setShowNewFlow] = useState(false);
  const [newFlowName, setNewFlowName] = useState("");
  const [renameFlowId, setRenameFlowId] = useState(null);
  const [renameFlowValue, setRenameFlowValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [wppTags, setWppTags] = useState([]);
  const [wppFilas, setWppFilas] = useState([]);
  const [wppOperators, setWppOperators] = useState([]);
  const [assistants, setAssistants] = useState([]);
  const [showAssistants, setShowAssistants] = useState(false);
  const fileInputRef = useRef(null);

  const ntypes = useMemo(() => nodeTypes, []);
  const defaultEdgeOptions = useMemo(() => ({ animated: true, style: { stroke: "#4ade80", strokeWidth: 2 }, markerEnd: { type: MarkerType.ArrowClosed, color: "#4ade80" } }), []);

  useEffect(() => { loadFlows(); loadWppData(); loadAssistants(); }, []);

  const loadAssistants = async () => {
    try { const r = await axios.get(`${API}/wpp/assistants`); setAssistants(r.data); } catch { setAssistants([]); }
  };

  const loadWppData = async () => {
    try {
      const [t, f] = await Promise.all([axios.get(`${API}/wpp/tags`), axios.get(`${API}/wpp/filas`)]);
      setWppTags(t.data); setWppFilas(f.data);
    } catch {}
    try { const u = await axios.get(`${API}/usuarios-sistema`); setWppOperators(u.data.usuarios || u.data || []); } catch { setWppOperators([]); }
  };

  const loadFlows = async () => {
    try { const r = await axios.get(`${API}/wpp/flows`); setFlows(r.data); } catch { }
  };

  const createFlow = async () => {
    if (!newFlowName.trim()) return;
    try {
      const startNode = { id: "start-1", type: "startNode", position: { x: 300, y: 50 }, data: { label: "Inicio" } };
      const r = await axios.post(`${API}/wpp/flows`, { nome: newFlowName.trim(), nodes: [startNode], edges: [] });
      setFlows(p => [r.data, ...p]); openFlow(r.data); setShowNewFlow(false); setNewFlowName(""); toast.success("Fluxo criado!");
    } catch { toast.error("Erro ao criar"); }
  };

  const openFlow = (flow) => {
    setCurrentFlow(flow);
    setNodes(flow.nodes || []);
    setEdges(flow.edges || []);
    setSelectedNode(null);
    setShowSimulator(false);
  };

  const saveFlow = async () => {
    if (!currentFlow) return;
    setSaving(true);
    try {
      await axios.put(`${API}/wpp/flows/${currentFlow.id}`, { nodes, edges });
      setCurrentFlow(p => ({ ...p, nodes, edges }));
      toast.success("Salvo!"); loadFlows();
    } catch { toast.error("Erro ao salvar"); }
    setSaving(false);
  };

  const deleteFlow = async (id) => {
    try { await axios.delete(`${API}/wpp/flows/${id}`);
      if (currentFlow?.id === id) { setCurrentFlow(null); setNodes([]); setEdges([]); }
      loadFlows(); toast.success("Excluido!");
    } catch { toast.error("Erro"); }
  };

  const duplicateFlow = async (id) => {
    try { const r = await axios.post(`${API}/wpp/flows/${id}/duplicate`); loadFlows(); toast.success("Duplicado!"); }
    catch { toast.error("Erro"); }
  };

  const openRename = (flow) => {
    setRenameFlowId(flow.id);
    setRenameFlowValue(flow.nome || "");
  };

  const saveRename = async () => {
    if (!renameFlowId || !renameFlowValue.trim()) return;
    try {
      await axios.put(`${API}/wpp/flows/${renameFlowId}`, { nome: renameFlowValue.trim() });
      if (currentFlow?.id === renameFlowId) setCurrentFlow(p => ({ ...p, nome: renameFlowValue.trim() }));
      setRenameFlowId(null); setRenameFlowValue("");
      loadFlows(); toast.success("Nome atualizado!");
    } catch { toast.error("Erro ao renomear"); }
  };

  const exportFlow = () => {
    if (!currentFlow) return;
    const data = JSON.stringify({ nome: currentFlow.nome, descricao: currentFlow.descricao, nodes, edges, config: currentFlow.config || {} }, null, 2);
    const blob = new Blob([data], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `flow-${currentFlow.nome}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const importFlow = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const r = await axios.post(`${API}/wpp/flows/import`, data);
      loadFlows(); openFlow(r.data); toast.success("Importado!");
    } catch { toast.error("Erro ao importar"); }
    e.target.value = "";
  };

  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);
  const onEdgeClick = useCallback((event, edge) => {
    event.stopPropagation();
    if (window.confirm("Excluir esta conexao?")) {
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
    }
  }, [setEdges]);

  const addNode = (type) => {
    const id = `${type}-${Date.now()}`;
    // Position near last node or center of viewport
    let posX = 300, posY = 200;
    if (nodes.length > 0) {
      const lastNode = nodes[nodes.length - 1];
      posX = lastNode.position.x + 50;
      posY = lastNode.position.y + 120;
    }
    const newNode = { id, type, position: { x: posX, y: posY }, data: { ...defaultNodeData[type] } };
    setNodes(nds => [...nds, newNode]);
  };

  const updateNodeData = (nodeId, data) => {
    setNodes(nds => nds.map(n => n.id === nodeId ? { ...n, data } : n));
    if (selectedNode?.id === nodeId) setSelectedNode(p => ({ ...p, data }));
  };

  const deleteNode = (nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
    if (selectedNode?.id === nodeId) setSelectedNode(null);
  };

  const duplicateNode = (nodeId) => {
    const orig = nodes.find(n => n.id === nodeId);
    if (!orig) return;
    const newId = `${orig.type}-${Date.now()}`;
    const clone = {
      ...orig,
      id: newId,
      position: { x: (orig.position?.x || 0) + 40, y: (orig.position?.y || 0) + 40 },
      data: { ...orig.data, label: `${orig.data?.label || ""} (copia)` },
      selected: false,
    };
    setNodes(nds => [...nds, clone]);
    setSelectedNode(clone);
    toast.success("Bloco duplicado!");
  };

  const onNodeClick = useCallback((_, node) => setSelectedNode(node), []);

  // ===== FLOW LIST VIEW =====
  if (!currentFlow) {
    return (
      <div className="h-full flex flex-col bg-[#0d1117]" data-testid="flow-builder-list">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <div className="flex items-center gap-2">
            {onBack && <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400" onClick={onBack}><ChevronLeft className="h-4 w-4" /></Button>}
            <Zap className="h-5 w-5 text-green-400" />
            <h2 className="text-lg font-bold text-white">Flow Builder</h2>
            <Badge className="bg-green-900 text-green-300 text-[10px]">{flows.length} fluxos</Badge>
          </div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="h-7 text-xs border-gray-700 text-gray-300 hover:text-white" onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-3 w-3 mr-1" /> Importar
            </Button>
            <Button size="sm" className="h-7 text-xs bg-green-600 hover:bg-green-700 gap-1" onClick={() => setShowNewFlow(true)} data-testid="btn-novo-flow">
              <Plus className="h-3 w-3" /> Novo Fluxo
            </Button>
            <input ref={fileInputRef} type="file" accept=".json" className="hidden" onChange={importFlow} />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {flows.length === 0 ? (
            <div className="text-center py-16 text-gray-500"><Zap className="h-12 w-12 mx-auto mb-3 opacity-20" /><p className="text-sm">Nenhum fluxo criado</p><p className="text-xs mt-1">Crie seu primeiro fluxo de automacao</p></div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
              {flows.map(f => (
                <div key={f.id} className="bg-[#161b22] border border-gray-800 rounded-lg p-4 hover:border-green-600 transition-colors cursor-pointer group" onClick={() => openFlow(f)} data-testid={`flow-card-${f.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2"><Zap className="h-4 w-4 text-green-400" /><span className="font-bold text-sm text-white">{f.nome}</span></div>
                    <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-green-400" onClick={() => openRename(f)} data-testid={`btn-editar-flow-${f.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-blue-400" onClick={() => duplicateFlow(f.id)}><Copy className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-400 hover:text-red-400" onClick={() => deleteFlow(f.id)}><Trash2 className="h-3 w-3" /></Button>
                    </div>
                  </div>
                  <p className="text-[10px] text-gray-500 mb-2">{f.descricao || "Sem descricao"}</p>
                  <div className="flex items-center gap-2">
                    <Badge className={`text-[9px] ${f.ativo ? "bg-green-900 text-green-300" : "bg-gray-800 text-gray-500"}`}>{f.ativo ? "Ativo" : "Inativo"}</Badge>
                    <span className="text-[9px] text-gray-600">{(f.nodes || []).length} blocos</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <Dialog open={showNewFlow} onOpenChange={setShowNewFlow}>
          <DialogContent className="max-w-sm bg-[#161b22] border-gray-800" data-testid="modal-novo-flow">
            <DialogHeader><DialogTitle className="text-white">Novo Fluxo</DialogTitle></DialogHeader>
            <div className="py-2"><Label className="text-gray-400">Nome</Label><Input className="bg-gray-800 border-gray-700 text-white" value={newFlowName} onChange={e => setNewFlowName(e.target.value)} placeholder="Ex: Boas Vindas" data-testid="input-flow-nome" /></div>
            <DialogFooter><Button variant="outline" className="border-gray-700 text-gray-300" onClick={() => setShowNewFlow(false)}>Cancelar</Button><Button className="bg-green-600 hover:bg-green-700" onClick={createFlow} data-testid="btn-criar-flow">Criar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={!!renameFlowId} onOpenChange={(o) => { if (!o) { setRenameFlowId(null); setRenameFlowValue(""); } }}>
          <DialogContent className="max-w-sm bg-[#161b22] border-gray-800" data-testid="modal-renomear-flow">
            <DialogHeader><DialogTitle className="text-white">Renomear Fluxo</DialogTitle></DialogHeader>
            <div className="py-2"><Label className="text-gray-400">Nome</Label><Input className="bg-gray-800 border-gray-700 text-white" value={renameFlowValue} onChange={e => setRenameFlowValue(e.target.value)} onKeyDown={e => { if (e.key === "Enter") saveRename(); }} autoFocus data-testid="input-renomear-flow" /></div>
            <DialogFooter><Button variant="outline" className="border-gray-700 text-gray-300" onClick={() => { setRenameFlowId(null); setRenameFlowValue(""); }}>Cancelar</Button><Button className="bg-green-600 hover:bg-green-700" onClick={saveRename} data-testid="btn-salvar-renomear">Salvar</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ===== FLOW EDITOR VIEW =====
  return (
    <div className="h-full flex flex-col bg-[#0d1117]" data-testid="flow-builder-editor">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-1.5 bg-[#161b22] border-b border-gray-800 shrink-0">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-400 hover:text-white" onClick={() => setCurrentFlow(null)}><ChevronLeft className="h-4 w-4" /></Button>
          <Zap className="h-4 w-4 text-green-400" />
          <span className="font-bold text-sm text-white truncate max-w-[200px]">{currentFlow.nome}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-7 text-[10px] text-gray-400 gap-1" onClick={exportFlow}><Download className="h-3 w-3" /> Exportar</Button>
          <Button size="sm" variant="ghost" className="h-7 text-[10px] text-emerald-400 gap-1" onClick={() => setShowAssistants(true)}><Sparkles className="h-3 w-3" /> Assistentes IA</Button>
          <Button size="sm" variant="ghost" className="h-7 text-[10px] text-purple-400 gap-1" onClick={() => setShowSimulator(!showSimulator)}>
            <Play className="h-3 w-3" /> {showSimulator ? "Fechar" : "Simular"}
          </Button>
          <Button size="sm" className="h-7 text-[10px] bg-green-600 hover:bg-green-700 gap-1" onClick={saveFlow} disabled={saving} data-testid="btn-salvar-flow">
            <Save className="h-3 w-3" /> {saving ? "Salvando..." : "Salvar"}
          </Button>
        </div>
      </div>
      <div className="flex-1 flex overflow-hidden">
        {/* Block Palette */}
        <div className="w-[140px] bg-[#161b22] border-r border-gray-800 p-2 shrink-0 overflow-y-auto">
          <p className="text-[9px] font-bold text-gray-500 uppercase mb-2 px-1">Blocos</p>
          <div className="space-y-1">
            {blockPalette.map(b => (
              <button key={b.type} onClick={() => addNode(b.type)}
                className="flex items-center gap-1.5 w-full px-2 py-1.5 rounded-md text-[10px] font-medium text-gray-300 hover:bg-gray-800 transition-colors"
                data-testid={`add-block-${b.type}`}>
                <div className="h-5 w-5 rounded flex items-center justify-center" style={{ backgroundColor: b.color + "30" }}>
                  <b.Icon className="h-3 w-3" style={{ color: b.color }} />
                </div>
                {b.label}
              </button>
            ))}
          </div>
        </div>
        {/* Canvas */}
        <div className="flex-1" data-testid="flow-canvas">
          <ReactFlow
            nodes={nodes} edges={edges}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect} onEdgeClick={onEdgeClick}
            defaultEdgeOptions={{ style: { strokeWidth: 2, cursor: "pointer" } }}
            onNodeClick={onNodeClick} onPaneClick={() => setSelectedNode(null)}
            nodeTypes={ntypes} defaultEdgeOptions={defaultEdgeOptions}
            fitView snapToGrid snapGrid={[15, 15]}
            style={{ background: "#0d1117" }}
          >
            <Background color="#1e293b" gap={20} size={1} />
            <Controls className="!bg-gray-800 !border-gray-700 !shadow-lg [&>button]:!bg-gray-800 [&>button]:!border-gray-700 [&>button]:!text-gray-300 [&>button:hover]:!bg-gray-700" />
            <MiniMap nodeColor={n => (nodeColors[n.type]?.bg || "#555")} maskColor="rgba(0,0,0,0.7)" className="!bg-gray-900 !border-gray-700" style={{width: 180, height: 120}} />
          </ReactFlow>
        </div>
        {/* Properties Panel - aparece so quando um no esta selecionado */}
        {selectedNode && (
          <div className="w-[480px] bg-[#161b22] border-l border-gray-800 shrink-0 overflow-y-auto relative" data-testid="properties-panel">
            <button
              className="absolute top-2 right-2 h-7 w-7 flex items-center justify-center rounded hover:bg-gray-800 text-gray-400 hover:text-white z-10"
              onClick={() => setSelectedNode(null)}
              title="Fechar painel"
              data-testid="btn-fechar-painel">
              <X className="h-4 w-4" />
            </button>
            <PropertiesPanel node={selectedNode} onChange={updateNodeData} onDelete={deleteNode} onDuplicate={duplicateNode} tags={wppTags} filas={wppFilas} operators={wppOperators} assistants={assistants} flows={flows} flowId={currentFlow?.id} />
          </div>
        )}
        {/* Simulator */}
        {showSimulator && (
          <div className="w-[300px] border-l border-gray-800 shrink-0">
            <ChatSimulator flow={{ ...currentFlow, nodes, edges }} onClose={() => setShowSimulator(false)} />
          </div>
        )}
      </div>
      {/* Assistants IA Dialog */}
      <Dialog open={showAssistants} onOpenChange={setShowAssistants}>
        <DialogContent className="max-w-lg bg-[#161b22] border-gray-800">
          <DialogHeader><DialogTitle className="text-white">Assistentes IA</DialogTitle></DialogHeader>
          <AssistantsManager assistants={assistants} onRefresh={loadAssistants} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
